import React, { useEffect } from "react";
import styled from "styled-components/macro";

import AlongLogo from "components/AlongLogo";
import TeacherSignupSteps from "./TeacherSignupSteps";
import { featureEnabled } from "utils/env";
import { SurfaceType } from "utils/analytics/analyticsConstants";
import { AnalyticsProvider } from "utils/analytics/AnalyticsContext";
import DevLogin from "../DevLogin";
import UserLoginModal from "../UserLoginModals/index";
import { LiveChat } from "components/LiveChat/LiveChat";
import Alert from "ui/Alert";
import { ErrorMessages } from "pages/UserLogin/UserLoginModals";
import GuestLayout from "components/GuestLayout/GuestLayout";
import { useLocation } from "react-router-dom";
import Inline from "ui/Inline/Inline";
import { TeacherAccount } from "ui/Icons";
import { IconProps } from "ui/SVGIcon";
import NavLink from "ui/router/NavLink";
import Typography from "ui/Typography/Typography";
import Stack from "ui/Stack";

const $AlongLogoContainer = styled.div`
  padding: 2rem 0;
`;

const $LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
`;

const $TeacherAccount = styled(TeacherAccount)<IconProps>`
  margin: 0 0.25rem 0 0.5rem;
`;

const $SignInLink = styled(NavLink)`
  color: inherit;
  text-decoration: underline;

  &:active,
  &:focus,
  &:hover {
    color: ${({ theme }) => theme.ColorBlurple500};
  }
`;

const $LoginContainer = styled(Stack).attrs({
  $align: "center",
  $space: "SizeMd",
})`
  padding: 2rem 0;
  text-align: center;

  .mb-4 {
    padding: 0;
  }
`;

function shouldEnableLiveChatWidget(loginError: string) {
  return (
    loginError === ErrorMessages.PENDING_ACCOUNT_LIVE_SUPPORT ||
    loginError === ErrorMessages.PENDING_ACCOUNT
  );
}

// There are several LegacyAuthError error messages that don't map to ErrorMessages enum.
// We show them as a red banner on top of the page directly.
function shouldShowAlertModal(loginError: string) {
  const arr: Array<string> = Object.values(ErrorMessages);
  return loginError && !arr.includes(loginError);
}

export default function TeacherSignup() {
  const location = useLocation();
  const [loginError, setLoginError] = React.useState("");
  const enableDevLogin = featureEnabled("ENABLE_DEV_LOGIN_ROUTES");
  const params = new URLSearchParams(location.search);
  const isExpeditedOnboarding =
    params.get("intent") === "expedited_educator_onboarding";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginErrorMessage = params.get("error") || "";
    setLoginError(loginErrorMessage);
    if (loginErrorMessage) {
      // clear the query param to dismiss the error
      window.history.pushState("clear", "", "?#");
    }
  }, [location]);

  return (
    <AnalyticsProvider surface={SurfaceType.USER_SIGNUP}>
      <GuestLayout userType="teacher">
        {shouldShowAlertModal(loginError) && (
          <Alert variant="danger">{loginError}</Alert>
        )}
        <$LoginContainer $align="center" $space="SizeXl">
          <$AlongLogoContainer>
            <AlongLogo size="standard" variant="along-2" />
          </$AlongLogoContainer>

          <UserLoginModal type={loginError} />
          <TeacherSignupSteps />
          {isExpeditedOnboarding && (
            <Inline alignY="center">
              <Typography size="bodySansSerif" fontWeight="bold">
                Already have an account?
              </Typography>
              <$LinkWrapper>
                <$TeacherAccount role="img" size="sm" title="account profile" />
                <$SignInLink
                  to="/?intent=expedited_educator_onboarding"
                  analyticsName="clicked_sign_in"
                >
                  Sign In
                </$SignInLink>
              </$LinkWrapper>
            </Inline>
          )}

          {enableDevLogin && (
            <DevLogin isOnDarkBackground={false} isEducatorSignup={true} />
          )}

          {shouldEnableLiveChatWidget(loginError) && (
            <LiveChat
              errorMessage={
                loginError === "pending_account"
                  ? ErrorMessages.PENDING_ACCOUNT
                  : ErrorMessages.PENDING_ACCOUNT_LIVE_SUPPORT
              }
            />
          )}
        </$LoginContainer>
      </GuestLayout>
    </AnalyticsProvider>
  );
}
